const uid = (value) => (value == null ? "" : String(value));

const REPLY_QUOTES_KEY = "messageReplyQuotes";
const REPLY_EMBED_REGEX = /^\[\[WLJ_REPLY:(.+?)\]\]/;

const parseReplyEmbedMeta = (raw) => {
  try {
    const meta = JSON.parse(raw);
    if (!meta || typeof meta.t !== "string") return null;
    return {
      replyChatID: meta.i ?? null,
      name: meta.n || "",
      text: meta.t,
      replyIsOwned: meta.o === 1,
    };
  } catch (e) {
    return null;
  }
};

export const parseEmbeddedReply = (msg) => {
  if (!msg || typeof msg !== "string") {
    return { body: msg || "", quote: null };
  }

  const match = msg.match(REPLY_EMBED_REGEX);
  if (!match) return { body: msg, quote: null };

  const quote = parseReplyEmbedMeta(match[1]);
  const body = msg.slice(match[0].length);
  if (!quote) return { body: msg, quote: null };

  return { body, quote };
};

export const getDisplayMessageText = (msg) => parseEmbeddedReply(msg).body;

export const embedReplyInMessage = (text, quote) => {
  if (!quote?.text || text == null) return text;

  const payload = JSON.stringify({
    i: quote.replyChatID ?? null,
    n: quote.name || "",
    t: quote.text,
    o: quote.replyIsOwned === true ? 1 : 0,
  });

  return `[[WLJ_REPLY:${payload}]]${text}`;
};

export const resolveEmbeddedQuoteName = (
  quote,
  message,
  partnerName,
  youLabel = "You"
) => {
  if (!quote) return partnerName || youLabel;

  if (message?.isOwned === true) {
    return quote.name || (quote.replyIsOwned ? youLabel : partnerName || youLabel);
  }

  if (quote.replyIsOwned === true) {
    return partnerName || youLabel;
  }

  return youLabel;
};

export const formatMessagePreviewText = (msg) => getDisplayMessageText(msg);

export const extractReplyReferenceId = (item) => {
  if (!item) return null;

  const nested = item.replyChat || item.parentChat || item.quotedChat || item.replyTo;
  if (nested?.chatID != null) return nested.chatID;
  if (nested?.ChatID != null) return nested.ChatID;

  return (
    item.replyChatID ??
    item.ReplyChatID ??
    item.parentChatID ??
    item.ParentChatID ??
    item.quotedChatID ??
    item.QuotedChatID ??
    item.refChatID ??
    item.RefChatID ??
    item.replyToChatID ??
    item.ReplyToChatID ??
    item.parentMsgID ??
    item.ParentMsgID ??
    item.replyToMsgID ??
    item.ReplyToMsgID ??
    item.refMsgID ??
    item.RefMsgID ??
    null
  );
};

export const extractReplyText = (item) => {
  if (!item) return "";

  const nested = item.replyChat || item.parentChat || item.quotedChat || item.replyTo;
  if (nested?.msg) return nested.msg;
  if (nested?.Msg) return nested.Msg;

  return (
    item.replyMsg ||
    item.ReplyMsg ||
    item.quotedMsg ||
    item.QuotedMsg ||
    item.parentMsg ||
    item.ParentMsg ||
    item.replyMessage ||
    item.ReplyMessage ||
    ""
  );
};

export const findChatInListById = (chatList, chatId) => {
  if (!Array.isArray(chatList) || chatId == null) return null;

  for (const group of chatList) {
    const found = (group?.chats || []).find(
      (chat) => String(chat.chatID) === String(chatId)
    );
    if (found) return found;
  }

  return null;
};

export const resolveQuoteNameFromReferencedChat = (
  referencedChat,
  partnerName,
  youLabel = "You",
  viewerUid = null
) => {
  if (!referencedChat) return partnerName || youLabel;

  const quotedOwnedByViewer =
    referencedChat.isOwned === true ||
    (viewerUid &&
      referencedChat.senderUid &&
      uid(referencedChat.senderUid) === uid(viewerUid));

  return quotedOwnedByViewer ? youLabel : partnerName || youLabel;
};

export const buildReplyQuoteFromMessage = (replyTo, partnerName, youLabel = "You") => {
  if (!replyTo?.chatID) return null;

  return {
    name: replyTo.isOwned ? youLabel : partnerName || youLabel,
    text: getDisplayMessageText(replyTo.msg) || "",
    replyChatID: replyTo.chatID,
    replyIsOwned: replyTo.isOwned === true,
  };
};

export const cacheMessageReplyQuote = (chatId, quote, partnerUid) => {
  if (chatId == null || !quote?.text || !partnerUid) return;

  try {
    const all = JSON.parse(localStorage.getItem(REPLY_QUOTES_KEY) || "{}");
    const partnerKey = String(partnerUid);
    all[partnerKey] = all[partnerKey] || {};
    all[partnerKey][String(chatId)] = quote;
    localStorage.setItem(REPLY_QUOTES_KEY, JSON.stringify(all));
  } catch (e) {
    // ignore storage errors
  }
};

export const getCachedMessageReplyQuote = (chatId, partnerUid) => {
  if (chatId == null || !partnerUid) return null;

  try {
    const all = JSON.parse(localStorage.getItem(REPLY_QUOTES_KEY) || "{}");
    return all[String(partnerUid)]?.[String(chatId)] || null;
  } catch (e) {
    return null;
  }
};

export const attachReplyFieldsToMessage = (message, quote) => {
  if (!message || !quote) return message;

  return {
    ...message,
    replyChatID: quote.replyChatID,
    parentChatID: quote.replyChatID,
    replyMsg: quote.text,
    parentMsg: quote.text,
    replyUserName: quote.name,
    replyIsOwned: quote.replyIsOwned === true,
  };
};

export const enrichChatWithReplyQuote = (
  chat,
  chatList,
  partnerName,
  youLabel = "You",
  viewerUid = null
) => {
  if (!chat || chat.isCompleteDeleted) return chat;

  const embedded = parseEmbeddedReply(chat.msg);
  if (embedded.quote) {
    return attachReplyFieldsToMessage(
      { ...chat, msg: embedded.body },
      {
        ...embedded.quote,
        name: resolveEmbeddedQuoteName(
          embedded.quote,
          chat,
          partnerName,
          youLabel
        ),
      }
    );
  }

  const refId = extractReplyReferenceId(chat);
  const referenced = refId ? findChatInListById(chatList, refId) : null;
  const quoteText =
    extractReplyText(chat) ||
    getDisplayMessageText(referenced?.msg) ||
    "";

  if (!quoteText) return chat;

  const quoteName =
    chat.replyUserName ||
    chat.ReplyUserName ||
    chat.parentUserName ||
    chat.ParentUserName ||
    resolveQuoteNameFromReferencedChat(
      referenced,
      partnerName,
      youLabel,
      viewerUid
    );

  return attachReplyFieldsToMessage(chat, {
    replyChatID: refId || referenced?.chatID,
    text: quoteText,
    name: quoteName,
    replyIsOwned:
      referenced?.isOwned === true ||
      (viewerUid &&
        referenced?.senderUid &&
        uid(referenced.senderUid) === uid(viewerUid)),
  });
};

export const enrichChatListWithReplyQuotes = (
  chatList,
  partnerName,
  youLabel = "You",
  viewerUid = null
) => {
  if (!Array.isArray(chatList)) return chatList;

  const cloned = chatList.map((group) => ({
    ...group,
    chats: (group?.chats || []).map((chat) => ({ ...chat })),
  }));

  return cloned.map((group) => ({
    ...group,
    chats: group.chats.map((chat) =>
      enrichChatWithReplyQuote(chat, cloned, partnerName, youLabel, viewerUid)
    ),
  }));
};

export const resolveMessageReplyQuote = (
  item,
  chatList,
  partnerUid,
  partnerName,
  youLabel = "You",
  viewerUid = null
) => {
  if (!item || item.isCompleteDeleted) return null;

  const embedded = parseEmbeddedReply(item.msg);
  if (embedded.quote) {
    return {
      name: resolveEmbeddedQuoteName(
        embedded.quote,
        item,
        partnerName,
        youLabel
      ),
      text: embedded.quote.text,
    };
  }

  const nested = item.replyChat || item.parentChat || item.quotedChat || item.replyTo;
  if (nested?.msg || nested?.Msg) {
    return {
      name:
        nested.isOwned === true
          ? youLabel
          : nested.senderName || nested.replyUserName || partnerName || youLabel,
      text: nested.msg || nested.Msg,
    };
  }

  const text = extractReplyText(item);
  if (text) {
    const refId = extractReplyReferenceId(item);
    const referenced = refId ? findChatInListById(chatList, refId) : null;

    return {
      name:
        item.replyUserName ||
        item.ReplyUserName ||
        item.parentUserName ||
        item.ParentUserName ||
        (referenced
          ? resolveQuoteNameFromReferencedChat(
              referenced,
              partnerName,
              youLabel,
              viewerUid
            )
          : item.replyIsOwned
            ? youLabel
            : partnerName) ||
        youLabel,
      text,
    };
  }

  const refId = extractReplyReferenceId(item);
  if (refId && Array.isArray(chatList)) {
    const found = findChatInListById(chatList, refId);
    if (found?.msg) {
      return {
        name: resolveQuoteNameFromReferencedChat(
          found,
          partnerName,
          youLabel,
          viewerUid
        ),
        text: getDisplayMessageText(found.msg),
      };
    }
  }

  return getCachedMessageReplyQuote(item.chatID, partnerUid);
};

export const buildReplyApiPayload = (replyTo) => {
  if (!replyTo?.chatID) return {};

  const id = replyTo.chatID;
  const text = replyTo.msg || "";

  return {
    replyChatID: id,
    parentChatID: id,
    ParentChatID: id,
    ReplyChatID: id,
    parentMsg: text,
    ParentMsg: text,
    replyMsg: text,
    ReplyMsg: text,
    quotedMsg: text,
    QuotedMsg: text,
    replyTo: {
      chatID: id,
      msg: text,
    },
  };
};

export const mergeReplyFieldsFromApi = (message, activeReply, partnerName, youLabel = "You") => {
  if (!message) return message;

  const apiText = extractReplyText(message);
  const apiRef = extractReplyReferenceId(message);

  if (apiText || apiRef) {
    return enrichChatWithReplyQuote(message, [], partnerName, youLabel);
  }

  if (!activeReply) return message;

  const quote = buildReplyQuoteFromMessage(activeReply, partnerName, youLabel);
  if (!quote) return message;

  return {
    ...attachReplyFieldsToMessage(message, quote),
    msg: embedReplyInMessage(getDisplayMessageText(message.msg), quote),
  };
};
